/**
 * Wire shapes of the change-request routes of Ark.Core's agentic facade (`api/agentic/v1`), mirroring
 * OrbitalWatch.Ark.Sync/Contracts/AgenticContracts.cs (camelCase JSON, envelope `{ data, error, meta }`).
 */

export type PushPolicy = "direct" | "propose";

export const ChangesetStates = {
    Applied: "applied",
    Proposed: "proposed",
    Approved: "approved",
    Rejected: "rejected",
    Failed: "failed",
    Withdrawn: "withdrawn",
} as const;

export interface AgenticSpaceDTO {
    id: number;
    title: string;
    description?: string;
    externalId?: string;
    projectId?: string;
    pageCount: number;
    /** "direct" | "propose"; null/absent means direct. */
    pushPolicy?: PushPolicy | null;
}

export interface ChangesetOpDTO {
    op: string;
    tempId?: string;
    id?: number;
    ifMatch?: string;
    clientKey?: string;
    title?: string;
    content?: string;
    clearContent?: boolean;
    rules?: Record<string, unknown>;
    /** A page id ("42"), a temp id ("new:3"), or null/"" for the space root. */
    parentRef?: string | null;
    index?: number;
    name?: string;
    sha256?: string;
    pageRef?: number;
}

export interface OpResultDTO {
    index: number;
    op: string;
    /** "ok" | "exists" | "failed" */
    status: string;
    id?: number;
    tempId?: string;
    fp?: string;
    etag?: string;
    version?: number;
    error?: string;
}

export interface ChangesetDTO {
    id: string;
    spaceId: number;
    state: string;
    message: string;
    client: string;
    authorId?: string | null;
    createdAt: string;
    appliedAt?: string | null;
    reviewedBy?: string | null;
    reviewReason?: string | null;
    opCount: number;
    failedOpIndex?: number | null;
    error?: string | null;
    effectiveChanged: number;
    results: OpResultDTO[];
    /** Counts from 1; each update (rebase) of a proposal makes a new one. Absent from older servers. */
    revision?: number;
    /** When the proposal was last updated (rebased); null until it is. */
    updatedAt?: string | null;
    /** Only on `GET changesets/{id}`; the list omits them. */
    ops?: ChangesetOpDTO[] | null;
    /**
     * Only on `GET changesets/{id}` of a proposal: each existing page its ops touch as it was when the change request was made.
     * Absent on direct pushes, on proposals from before Ark kept bases, and from servers that do not keep them.
     */
    bases?: PageBaseDTO[] | null;
}

/** A page as a change request found it (`ChangesetDTO.bases`): what its author changed is base → proposal. */
export interface PageBaseDTO {
    pageId: number;
    /** The etag of that version, the one the ops' `ifMatch` carry. */
    etag: string;
    title: string;
    rules?: Record<string, unknown> | null;
    content?: string | null;
}

/** One reason a changeset cannot be applied as it stands (`code`: "stale", "missing", "parent-missing"…). */
export interface ChangesetIssueDTO {
    index: number;
    op: string;
    pageId?: number | null;
    code: string;
    message: string;
    expected?: string | null;
    actual?: string | null;
}

export const RebaseOutcomes = {
    Rebased: "rebased",
    UpToDate: "up-to-date",
    Conflicts: "conflicts",
} as const;

/** A change of the author that could not be merged into the page as it is now (`POST changesets/{id}/rebase`). */
export interface ChangesetConflictDTO {
    pageId: number;
    /** Index of the op that carries the change. */
    index: number;
    /** "title" | "rules" | "content" | "page" */
    field: string;
    /** "both-changed" | "changed-before-delete" | "deleted" | "no-base" */
    code: string;
    message: string;
    /** For rules: the key that conflicts. */
    path?: string | null;
    /** For content: the first line of the base the conflicting lines cover (1-based). */
    line?: number | null;
    /**
     * For content: the first line of the merged content (`ChangesetMergeDTO.content`) where the author's lines of this conflict sit
     * (1-based); `proposed` tells how many (none when the author deleted the run).
     */
    mergedLine?: number | null;
    /** As the author found it, as the author proposes it, as it is now (rules as JSON, content as the lines). */
    base?: string | null;
    proposed?: string | null;
    current?: string | null;
}

export const ResolutionChoices = {
    /** Keep the author's version (for a delete or clear: do it anyway). */
    Ours: "ours",
    /** Take the page as it is now (for a cleared content: keep the page's content). */
    Theirs: "theirs",
    /** Use `value`: a title, a rule as JSON, or the lines. */
    Manual: "manual",
} as const;

export type ResolutionChoice = (typeof ResolutionChoices)[keyof typeof ResolutionChoices];

/** How the author settles one conflict, named as the server reported it (page, op, field, rule path or base line). */
export interface ChangesetResolutionDTO {
    pageId: number;
    index: number;
    field: string;
    path?: string | null;
    line?: number | null;
    choice: ResolutionChoice;
    /** For "manual": the title, the rule as JSON (null removes it), or the lines ("" removes the run). */
    value?: string | null;
}

/** Body of `POST changesets/{id}/rebase`; without one, the rebase merges and saves. */
export interface ChangesetRebaseRequestDTO {
    /** Report what a rebase would do without saving anything. */
    dryRun?: boolean;
    /** The author's answers to the conflicts an earlier call reported. */
    resolutions?: ChangesetResolutionDTO[];
}

/**
 * One update op's values merged with the page's edits since the proposal: what the op carries once rebased or, where it conflicts, the
 * merge with the author's version in the conflicting places (`ChangesetConflictDTO.mergedLine` says where).
 */
export interface ChangesetMergeDTO {
    pageId: number;
    /** Index of the update op. */
    index: number;
    /** Nothing in this op conflicts (after the resolutions). */
    clean: boolean;
    /** Null when the op leaves the title alone. */
    title?: string | null;
    /** Null when the op leaves the rules alone. */
    rules?: Record<string, unknown> | null;
    /** The merged content ("" when the page ends up empty); null when the op leaves the body alone. */
    content?: string | null;
}

/**
 * `POST changesets/{id}/rebase`: the proposal brought up to date with its pages (all or nothing), or what stopped it.
 * `GET changesets/{id}/rebase` (and `dryRun`) report the same without saving: what an update would do.
 */
export interface ChangesetRebaseDTO {
    /** "rebased" | "up-to-date" | "conflicts"; on a dry run, what a rebase would be. */
    outcome: string;
    /** Nothing was saved: `changeset` is the proposal as stored. */
    dryRun?: boolean;
    /** The pages whose changes were (or would be) merged. */
    pages: number[];
    conflicts: ChangesetConflictDTO[];
    /** The merge of each update op on a page edited since the proposal, clean or not. Absent from servers that do not send it. */
    merges?: ChangesetMergeDTO[] | null;
    /** What still stops an approve (what a rebase does not fix). */
    issues: ChangesetIssueDTO[];
    /** The changeset with its ops and bases. */
    changeset?: ChangesetDTO | null;
}

/** The part of a manifest page the review reads (`GET spaces/{s}/manifest` → `pages[]`). */
export interface SpacePageDTO {
    id: number;
    parentId?: number | null;
    orderIndex?: number;
    title: string;
    /** The page's current etag; an op whose `ifMatch` no longer matches it fails on approve. */
    etag?: string;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    rules?: Record<string, unknown> | null;
}

/** `POST spaces/{s}/pages/content` → one page body. */
export interface PageContentDTO {
    id: number;
    content?: string | null;
    fp?: string;
    etag?: string;
}

export interface WhoAmIDTO {
    id?: string;
    name?: string;
    client?: string;
}

/** Body of `POST changesets/{id}/approve`; optional. */
export interface ApproveRequestDTO {
    /** The revision the reviewer read: refused (409 `changeset-changed`) when the proposal moved on since. */
    revision?: number;
    /** Bring the proposal up to date first (a rebase, which must merge without conflicts), then apply it. */
    update?: boolean;
}

/** One revision of a change request: as proposed, then one per update, with what the update merged and settled. */
export interface ChangesetRevisionDTO {
    revision: number;
    /** How it came to be: "proposed", "updated" (brought up to date with its pages), "amended" (the author replaced what it proposes). */
    kind?: string;
    createdAt: string;
    /** Who made it: the author, or the reviewer who updated a cleanly merging proposal. */
    actor?: string | null;
    /** The pages the update merged (empty for revision 1). */
    pages: number[];
    /** The conflicts the update settled, and how. */
    resolutions: ChangesetResolutionDTO[];
    /** The ops at this revision; only on `GET …/revisions/{n}`. */
    ops?: ChangesetOpDTO[] | null;
}

/** `GET users?ids=a,b`: the display name of each id the directory knows. */
export interface UserNameDTO {
    id: string;
    displayName: string;
    email?: string | null;
}
